/**
 * Origins permitted to drive extension auth state (set/clear the chrome.storage hub).
 *
 * Only the Blueticks web app may set or clear an auth token. web.whatsapp.com is
 * intentionally EXCLUDED even though the extension injects a content script there and
 * externally_connectable lists it — otherwise any script running on web.whatsapp.com
 * could forge a `set-token` (token injection -> the popup logs in as the attacker) or a
 * `web-logout`/`logout` (forced logout). The page->content `postMessage` relays and the
 * background `onMessageExternal` handler both gate auth-mutating cmds through here.
 *
 * Lives in bt-common (the shared low-level layer imported by extension + backend) so the
 * auth-bridge contract is the single strict-typed source from extension to backend, with
 * no cross-layer imports.
 */

/**
 * Known auth-bridge commands — the message-service counterpart to the WhatsApp tools
 * `WaToolContractMap` (and the v1 API / MQTT command registries). Auth state is driven
 * ONLY by these named commands; each maps to a typed payload, shared across the page,
 * content-script, and background contexts. No stringly-typed `cmd` / `any` payloads.
 */
export const AUTH_BRIDGE_COMMANDS = {
  SET_TOKEN: "set-token",
  WEB_LOGOUT: "web-logout",
  LOGOUT: "logout",
  CLEAR_LOCAL_TOKEN: "clear-local-token",
} as const;

export type AuthBridgeCommand = (typeof AUTH_BRIDGE_COMMANDS)[keyof typeof AUTH_BRIDGE_COMMANDS];

/**
 * Per-command payload contract (cmd -> payload), mirroring WaToolContractMap. `set-token`
 * carries the JWT; the logout signals carry no payload.
 */
export interface AuthBridgeContractMap {
  [AUTH_BRIDGE_COMMANDS.SET_TOKEN]: { accessToken: string };
  [AUTH_BRIDGE_COMMANDS.WEB_LOGOUT]: Record<string, never>;
  [AUTH_BRIDGE_COMMANDS.LOGOUT]: Record<string, never>;
  [AUTH_BRIDGE_COMMANDS.CLEAR_LOCAL_TOKEN]: Record<string, never>;
}

/** Typed payload for a given command (e.g. AuthBridgePayload<'set-token'> = { accessToken: string }). */
export type AuthBridgePayload<C extends AuthBridgeCommand> = AuthBridgeContractMap[C];

/** Response shape of the `get-token` background query (the hub's current token, or null). */
export interface GetTokenResponse {
  accessToken?: string | null;
}

/**
 * A typed auth-bridge message as it travels page <-> content <-> background. The token
 * has historically been sent both nested under `data` and at the top level, so both are
 * typed here (readers prefer `data.accessToken`, falling back to the top-level form).
 */
export interface AuthBridgeMessage {
  cmd?: AuthBridgeCommand | string;
  accessToken?: string;
  data?: { accessToken?: string };
}

/** Window-relay cmds the web app posts from the page world (no chrome.runtime.id). */
export const AUTH_BRIDGE_CMDS: string[] = [AUTH_BRIDGE_COMMANDS.SET_TOKEN, AUTH_BRIDGE_COMMANDS.WEB_LOGOUT];

/** Any cmd that mutates the auth hub (used to gate the background external path). */
export const AUTH_SENSITIVE_CMDS: string[] = [AUTH_BRIDGE_COMMANDS.SET_TOKEN, AUTH_BRIDGE_COMMANDS.WEB_LOGOUT, AUTH_BRIDGE_COMMANDS.LOGOUT];

/**
 * True if `origin` is a trusted Blueticks web-app origin allowed to mutate auth.
 * Accepts an origin string (e.g. "https://app.blueticks.co") or a full URL (sender.url).
 */
export function isTrustedAuthOrigin(origin?: string | null): boolean {
  if (!origin) return false;
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return false;
  }
  const { hostname, protocol } = url;

  // http is allowed only for local dev (localhost / 127.0.0.1).
  if (protocol === "http:") {
    return hostname === "localhost" || hostname === "127.0.0.1";
  }
  if (protocol !== "https:") return false;

  return (
    hostname === "blueticks.co" ||
    hostname === "app.blueticks.co" ||
    hostname.endsWith(".blueticks.co") ||
    // QA web-app deployment (dev/staging manifest only).
    hostname === "blueticks-qa.netlify.app"
  );
}
